import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getSummary, getProducts } from '../utils/api';
import { fmt } from '../utils/format';
import ProductThumb from '../components/ProductThumb';

export default function DashboardPage() {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['summary'],
    queryFn: () => getSummary().then((r) => r.data),
    refetchInterval: 30000,
  });

  const { data: prodData } = useQuery({
    queryKey: ['products', { low_stock: 'true' }],
    queryFn: () => getProducts({ low_stock: 'true' }).then((r) => r.data),
  });

  const lowStockItems = prodData?.products || [];
  const today = summary?.today || {};
  const weekly = summary?.weekly || [];
  const topProds = summary?.topProducts || [];
  const cashiers = summary?.cashiers || [];

  const chartData = weekly.map((entry) => ({
    day: new Date(entry.day).toLocaleDateString('en-KE', { weekday: 'short' }),
    revenue: parseFloat(entry.revenue),
    tx: parseInt(entry.tx, 10),
  }));

  const topMap = new Map((prodData?.products || []).map((product) => [product.name, product]));

  return (
    <div className="p-page fade-up">
      <div className="stats-grid-4 mb-16">
        {[
          { label: "Today's Revenue", val: fmt.currency(today.revenue), change: 'Live', up: true, icon: 'KES', bg: 'var(--brand-lt)' },
          { label: 'Transactions Today', val: fmt.number(today.tx), change: 'completed sales', up: null, icon: 'TX', bg: 'var(--green-lt)' },
          { label: 'Low Stock Items', val: lowStockItems.length, change: lowStockItems.length > 0 ? 'Need reorder' : 'All good', up: lowStockItems.length === 0, icon: 'LOW', bg: 'var(--amber-lt)' },
          { label: 'Active Cashiers', val: cashiers.filter((cashier) => cashier.tx_today > 0).length, change: 'with sales today', up: null, icon: 'STAFF', bg: 'var(--teal-lt)' },
        ].map((stat, index) => (
          <div className="stat-card fade-up" key={stat.label} style={{ animationDelay: `${index * 0.07}s` }}>
            <div className="flex-between mb-12">
              <div>
                {isLoading ? <div style={{ width: 80, height: 28, background: 'var(--bg2)', borderRadius: 6, marginBottom: 6 }} /> : <div className="stat-kpi">{stat.val}</div>}
                <div className="stat-label">{stat.label}</div>
              </div>
              <div className="stat-icon" style={{ background: stat.bg, fontSize: 10, fontWeight: 700 }}>{stat.icon}</div>
            </div>
            <div className={`stat-change ${stat.up === true ? 'up' : stat.up === false ? 'dn' : 'nt'}`}>{stat.change}</div>
          </div>
        ))}
      </div>

      <div className="dash-grid-2 mb-16">
        <div className="card fade-up" style={{ animationDelay: '.28s' }}>
          <div className="card-head">
            <div>
              <div className="card-title">Weekly revenue</div>
              <div className="card-sub">{fmt.currency(chartData.reduce((sum, day) => sum + day.revenue, 0))} this week</div>
            </div>
            <span className="badge badge-green">Live</span>
          </div>
          <div className="card-pad" style={{ paddingTop: 10 }}>
            {chartData.length === 0 ? (
              <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink4)', fontSize: 13 }}>No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'var(--ink3)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--ink4)' }} axisLine={false} tickLine={false} tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => [fmt.currency(value), 'Revenue']} labelStyle={{ color: 'var(--ink)', fontWeight: 600 }} contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }} />
                  <Bar dataKey="revenue" fill="var(--brand)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card fade-up" style={{ animationDelay: '.32s' }}>
          <div className="card-head"><div className="card-title">Top products today</div></div>
          <div style={{ padding: '8px 0' }}>
            {topProds.length === 0 ? (
              <div className="empty-state" style={{ padding: '24px' }}><div className="empty-title">No sales today yet</div><div className="empty-sub">Products will appear here after the first sale.</div></div>
            ) : topProds.map((product, index) => {
              const details = topMap.get(product.product_name) || { name: product.product_name };
              return (
                <div key={product.product_name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: index < topProds.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink4)', minWidth: 20 }}>#{index + 1}</div>
                  <ProductThumb product={details} size="sm" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{product.product_name}</div>
                    <div className="progress-track"><div className="progress-fill" style={{ width: `${(product.total_qty / topProds[0]?.total_qty) * 100}%` }} /></div>
                  </div>
                  <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--ink3)' }}>{product.total_qty} sold</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="dash-grid-r">
        <div className="card fade-up" style={{ animationDelay: '.36s' }}>
          <div className="card-head"><div className="card-title">Cashier performance today</div></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Cashier</th><th>Role</th><th>Transactions</th><th>Revenue</th></tr></thead>
              <tbody>
                {cashiers.length === 0 ? (
                  <tr><td colSpan="4"><div className="empty-state" style={{ padding: 24 }}><div className="empty-title">No cashier activity today</div></div></td></tr>
                ) : cashiers.map((cashier) => (
                  <tr key={cashier.full_name}>
                    <td>
                      <div className="flex gap-8">
                        <div className={`avatar av-sm ${cashier.role === 'supervisor' ? 'av-teal' : 'av-green'}`}>{cashier.avatar_initials}</div>
                        <span className="td-main">{cashier.full_name}</span>
                      </div>
                    </td>
                    <td><span className={`badge ${cashier.role === 'supervisor' ? 'badge-teal' : 'badge-gray'}`} style={{ textTransform: 'capitalize' }}>{cashier.role}</span></td>
                    <td>{cashier.tx}</td>
                    <td className="td-mono td-main">{fmt.currency(cashier.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card fade-up" style={{ animationDelay: '.40s' }}>
          <div className="card-head">
            <div className="card-title">Stock alerts</div>
            <span className="badge badge-amber">{lowStockItems.length} items</span>
          </div>
          <div>
            {lowStockItems.length === 0 ? (
              <div className="empty-state" style={{ padding: 24 }}><div className="empty-title">All items in stock</div></div>
            ) : lowStockItems.slice(0, 6).map((product, index) => (
              <div key={product.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '11px 20px', borderBottom: index < Math.min(lowStockItems.length, 6) - 1 ? '1px solid var(--border)' : 'none' }}>
                <div className="flex gap-8" style={{ minWidth: 0 }}>
                  <ProductThumb product={product} size="sm" />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{product.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink4)', marginTop: 2 }}>{product.stock} left, min: {product.low_stock_at}</div>
                  </div>
                </div>
                <span className={`badge ${product.stock === 0 ? 'badge-red' : 'badge-amber'}`}>{product.stock === 0 ? 'Out' : 'Low'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
